import {
  convertDimensionMagnitude,
  type CapacityUnit,
  type DimensionsLike,
  type DimensionUnit,
} from "@shared/formats/dimensions";

export type SizeOption = { key: string; label: string };

type Footprint = { long: number; short: number };

type MeasuredSize = {
  inches: { long: number; short: number; height: number | undefined } | null;
  millimetres: Footprint | null;
  capacityGallons: number | undefined;
};

type FootprintStandard = SizeOption & Footprint;

const INCH_TOLERANCE = 0.6;
const MILLIMETRE_TOLERANCE = 15;
const CAPACITY_TOLERANCE = 0.1;
const LITRES_PER_GALLON = 3.785;

const PALLET_FOOTPRINTS: readonly FootprintStandard[] = [
  { key: "48x40", label: "48 × 40 in (GMA)", long: 48, short: 40 },
  { key: "48x48", label: "48 × 48 in", long: 48, short: 48 },
  { key: "48x45", label: "48 × 45 in", long: 48, short: 45 },
  { key: "48x42", label: "48 × 42 in", long: 48, short: 42 },
  { key: "45x45", label: "45 × 45 in", long: 45, short: 45 },
  { key: "43x43", label: "43 × 43 in", long: 43, short: 43 },
  { key: "42x42", label: "42 × 42 in", long: 42, short: 42 },
  { key: "40x40", label: "40 × 40 in", long: 40, short: 40 },
  { key: "36x36", label: "36 × 36 in", long: 36, short: 36 },
  { key: "48x32", label: "48 × 32 in", long: 48, short: 32 },
];

const METRIC_PALLET_FOOTPRINTS: readonly FootprintStandard[] = [
  { key: "1200x800mm", label: "1200 × 800 mm (EUR)", long: 1200, short: 800 },
  { key: "1200x1000mm", label: "1200 × 1000 mm", long: 1200, short: 1000 },
  { key: "1100x1100mm", label: "1100 × 1100 mm", long: 1100, short: 1100 },
];

const GAYLORD_FOOTPRINTS: readonly FootprintStandard[] = [
  { key: "48x40", label: "48 × 40 in", long: 48, short: 40 },
  { key: "48x44", label: "48 × 44 in", long: 48, short: 44 },
  { key: "48x45", label: "48 × 45 in", long: 48, short: 45 },
  { key: "48x48", label: "48 × 48 in", long: 48, short: 48 },
  { key: "40x40", label: "40 × 40 in", long: 40, short: 40 },
];

const GAYLORD_HEIGHT_BANDS = [
  { suffix: "24-30", label: "24–30 in tall", max: 30.5 },
  { suffix: "31-39", label: "31–39 in tall", max: 39.5 },
  { suffix: "40-45", label: "40–45 in tall", max: 45.5 },
  { suffix: "46-plus", label: "46 in or taller", max: Number.POSITIVE_INFINITY },
] as const;

const IBC_CAPACITIES = [275, 330] as const;
const DRUM_CAPACITIES = [55, 30, 15] as const;
const IBC_MAX_275_HEIGHT = 49.5;
const DRUM_HEIGHT_FLOORS: ReadonlyArray<{ gallons: number; minHeight: number }> = [
  { gallons: 55, minHeight: 33 },
  { gallons: 30, minHeight: 27 },
  { gallons: 15, minHeight: 20 },
];

const FOOTPRINT_TYPES = new Set(["pallets", "plastic-pallets"]);
const DRUM_TYPES = new Set(["plastic-drums", "metal-drums"]);

const UNIT_ALIASES = new Map<string, DimensionUnit>([
  ["mm", "mm"],
  ["cm", "cm"],
  ["in", "in"],
  ["inch", "in"],
  ["inches", "in"],
  ['"', "in"],
  ["″", "in"],
  ["ft", "ft"],
  ["feet", "ft"],
  ["'", "ft"],
]);

function readUnit(raw: string): DimensionUnit | undefined {
  const [token] = raw.toLowerCase().match(/mm|cm|inches|inch|in|ft|feet|"|″|'/) ?? [];
  return token ? UNIT_ALIASES.get(token) : undefined;
}

function readCapacityUnit(raw: string): CapacityUnit {
  const lowered = raw.toLowerCase();
  if (/\b(?:lbs?|pounds?)\b/.test(lowered)) return "lbs";
  if (/\d\s*l\b|\blit(?:re|er)s?\b/.test(lowered)) return "L";
  return "gal";
}

function parseRaw(raw: string): DimensionsLike | null {
  const numbers = (raw.match(/\d+(?:\.\d+)?/g) ?? []).map(Number);
  if (numbers.length === 0) return null;
  const [length, width, height] = numbers;
  if (numbers.length === 1) return { capacity: length, capacityUnit: readCapacityUnit(raw) };
  return { length, width, height, unit: readUnit(raw) ?? "in" };
}

function toGallons(dims: DimensionsLike): number | undefined {
  if (dims.capacity === undefined || dims.capacity <= 0) return undefined;
  if (dims.capacityUnit === "L") return dims.capacity / LITRES_PER_GALLON;
  if (dims.capacityUnit === "lbs") return undefined;
  return dims.capacity;
}

function measure(dims: DimensionsLike): MeasuredSize {
  const parsed =
    dims.length === undefined && dims.width === undefined && dims.raw
      ? (parseRaw(dims.raw) ?? dims)
      : dims;
  const capacityGallons = toGallons(parsed);
  if (!parsed.length || !parsed.width) {
    return { inches: null, millimetres: null, capacityGallons };
  }
  const unit = parsed.unit ?? "in";
  const inch = (value: number) => convertDimensionMagnitude(value, unit, "in");
  const long = Math.max(parsed.length, parsed.width);
  const short = Math.min(parsed.length, parsed.width);
  return {
    inches: {
      long: inch(long),
      short: inch(short),
      height: parsed.height ? inch(parsed.height) : undefined,
    },
    millimetres:
      unit === "mm" || unit === "cm"
        ? {
            long: convertDimensionMagnitude(long, unit, "mm"),
            short: convertDimensionMagnitude(short, unit, "mm"),
          }
        : null,
    capacityGallons,
  };
}

function matchFootprint(
  standards: readonly FootprintStandard[],
  footprint: Footprint,
  tolerance: number,
): FootprintStandard | undefined {
  return standards.find(
    (standard) =>
      Math.abs(standard.long - footprint.long) <= tolerance &&
      Math.abs(standard.short - footprint.short) <= tolerance,
  );
}

function roundedFootprintKey(footprint: Footprint): string {
  return `${Math.round(footprint.long)}x${Math.round(footprint.short)}`;
}

function palletKey(size: MeasuredSize): string | null {
  if (size.millimetres) {
    const metric = matchFootprint(METRIC_PALLET_FOOTPRINTS, size.millimetres, MILLIMETRE_TOLERANCE);
    if (metric) return metric.key;
  }
  if (!size.inches) return null;
  return (
    matchFootprint(PALLET_FOOTPRINTS, size.inches, INCH_TOLERANCE)?.key ??
    roundedFootprintKey(size.inches)
  );
}

function gaylordKey(size: MeasuredSize): string | null {
  if (!size.inches) return null;
  const footprint =
    matchFootprint(GAYLORD_FOOTPRINTS, size.inches, INCH_TOLERANCE)?.key ??
    roundedFootprintKey(size.inches);
  const { height } = size.inches;
  if (height === undefined) return footprint;
  const band = GAYLORD_HEIGHT_BANDS.find((candidate) => height <= candidate.max);
  return band ? `${footprint}x${band.suffix}` : footprint;
}

function nearestCapacity(
  gallons: number | undefined,
  standards: readonly number[],
): number | undefined {
  if (gallons === undefined) return undefined;
  return standards.find(
    (standard) => Math.abs(gallons - standard) <= standard * CAPACITY_TOLERANCE,
  );
}

function ibcKey(size: MeasuredSize): string | null {
  const byCapacity = nearestCapacity(size.capacityGallons, IBC_CAPACITIES);
  if (byCapacity) return `${byCapacity}gal`;
  const height = size.inches?.height;
  if (height === undefined) return null;
  return height <= IBC_MAX_275_HEIGHT ? "275gal" : "330gal";
}

function drumKey(size: MeasuredSize): string | null {
  const byCapacity = nearestCapacity(size.capacityGallons, DRUM_CAPACITIES);
  if (byCapacity) return `${byCapacity}gal`;
  const height = size.inches?.height;
  if (height === undefined) return null;
  const floor = DRUM_HEIGHT_FLOORS.find((candidate) => height >= candidate.minHeight);
  return floor ? `${floor.gallons}gal` : null;
}

export function deriveSizeKey(
  productTypeSlug: string,
  dims: DimensionsLike | null | undefined,
): string | null {
  if (!dims) return null;
  const size = measure(dims);
  if (FOOTPRINT_TYPES.has(productTypeSlug)) return palletKey(size);
  if (productTypeSlug === "gaylord-boxes") return gaylordKey(size);
  if (productTypeSlug === "ibc-totes") return ibcKey(size);
  if (DRUM_TYPES.has(productTypeSlug)) return drumKey(size);
  return null;
}

function gaylordOptions(): SizeOption[] {
  return GAYLORD_FOOTPRINTS.flatMap((footprint) =>
    GAYLORD_HEIGHT_BANDS.map((band) => ({
      key: `${footprint.key}x${band.suffix}`,
      label: `${footprint.label}, ${band.label}`,
    })),
  );
}

function capacityOptions(capacities: readonly number[]): SizeOption[] {
  return capacities.map((gallons) => ({ key: `${gallons}gal`, label: `${gallons} gal` }));
}

export function standardSizeOptions(productTypeSlug: string): SizeOption[] {
  if (FOOTPRINT_TYPES.has(productTypeSlug)) {
    return [...PALLET_FOOTPRINTS, ...METRIC_PALLET_FOOTPRINTS].map(({ key, label }) => ({ key, label }));
  }
  if (productTypeSlug === "gaylord-boxes") return gaylordOptions();
  if (productTypeSlug === "ibc-totes") return capacityOptions(IBC_CAPACITIES);
  if (DRUM_TYPES.has(productTypeSlug)) return capacityOptions(DRUM_CAPACITIES);
  return [];
}

export function isSizeIndexed(productTypeSlug: string): boolean {
  return standardSizeOptions(productTypeSlug).length > 0;
}

export function isStandardSizeKey(productTypeSlug: string, key: string): boolean {
  return standardSizeOptions(productTypeSlug).some((option) => option.key === key);
}

export function sizeKeyLabel(productTypeSlug: string, key: string): string {
  const standard = standardSizeOptions(productTypeSlug).find((option) => option.key === key);
  if (standard) return standard.label;
  if (key.endsWith("mm")) return `${key.slice(0, -2).replace("x", " × ")} mm`;
  return `${key.replaceAll("x", " × ")} in`;
}
